import React from 'react';

import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { Subscription } from 'src/features/subscriptions/utils/types';
import { useEndSubscriptionTrial } from 'src/features/subscriptions/api/useEndSubscriptionTrial';
import { LIST_SUBSCRIPTIONS_RESOURCE } from 'src/features/subscriptions/api/useListSubscriptions';
import { SUBSCRIPTION_RESOURCE } from 'src/features/subscriptions/api/useGetSubscription';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';

interface EndSubscriptionTrialFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscription: Subscription;
    children?: React.ReactNode;
}

function EndSubscriptionTrialFormModal<TData>({
    selectedSubscription,
    children,
}: EndSubscriptionTrialFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: endTrial } = useEndSubscriptionTrial({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTIONS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subscriptionsMessages.endTrialSuccess),
                description: formatMessage(subscriptionsMessages.endTrialSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            // `subscription_not_in_trial` arrives with attr: null, so it is a toast, not a field error.
            const errorDetails = error.response?.data?.errors[0]?.detail;
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorDetails || formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subscriptionsMessages.endTrial },
                subTitle: {
                    ...subscriptionsMessages.endTrialSubtitle,
                    values: {
                        entity: selectedSubscription.reference,
                    },
                },
                icon: ICON_MAP.SCHEDULED,
                confirmationMessage: { ...subscriptionsMessages.confirmEndTrial },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: subscriptionsMessages.endTrial,
                callbackFn: () => {
                    return (
                        endTrial({
                            subscriptionId: selectedSubscription.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default EndSubscriptionTrialFormModal;
